import { Fragment, createElement, forwardRef, useMemo, type ReactNode } from "react";
import { AI_CHANGED, BLANK, renderValue, stripAiMarks, type ContractField } from "@/lib/library-client";
import "./library.css";

const TOKEN = /\{\{(f\d+)\}\}/g;
const ALLOWED = new Set(["h1", "h2", "h3", "p", "b", "strong", "i", "em", "u", "br", "table", "tbody", "thead", "tr", "td", "th", "ul", "ol", "li"]);
/** Types edited right inside the document; the others (date, options, long texts) open the side panel. */
const INLINE_TYPES = new Set(["text", "name", "address", "cpf", "cnpj", "rg", "cep", "phone", "email", "money", "number", "percent"]);

interface TemplateViewProps {
  html: string;
  fields: ContractField[];
  values: Record<string, string>;
  activeKey: string | null;
  onChange: (key: string, value: string) => void;
  onFocusField: (key: string) => void;
  /** Document language: values of an English copy are written the English way (March 12, 2026; 1,500.00). */
  lang?: string;
  /** Show what the last AI adjustment changed (blocks marked ai-changed). */
  highlight?: boolean;
}

/** The model as a sheet of paper with one input per placeholder. */
export function TemplateView({ html, fields, values, activeKey, onChange, onFocusField, lang = "pt", highlight = true }: TemplateViewProps) {
  const byKey = useMemo(() => new Map(fields.map((f) => [f.key, f])), [fields]);
  const doc = useMemo(() => new DOMParser().parseFromString(`<body>${html}</body>`, "text/html"), [html]);

  let counter = 0;
  const renderNode = (node: Node): ReactNode => {
    if (node.nodeType === Node.TEXT_NODE) return renderText(node.textContent || "");
    if (node.nodeType !== Node.ELEMENT_NODE) return null;
    const el = node as HTMLElement;
    const tag = el.tagName.toLowerCase();
    const children = Array.from(el.childNodes).map((c, i) => <Fragment key={i}>{renderNode(c)}</Fragment>);
    if (!ALLOWED.has(tag)) return <>{children}</>;
    if (tag === "br") return <br />;
    const props: Record<string, unknown> = { key: counter++ };
    const cls = highlight ? el.className : el.className.split(/\s+/).filter((c) => c && c !== AI_CHANGED).join(" ");
    if (cls) props.className = cls;
    if (tag === "td" || tag === "th") {
      const span = el.getAttribute("colspan");
      if (span) props.colSpan = Number(span);
    }
    return createElement(tag, props, children);
  };

  const renderText = (text: string): ReactNode => {
    const parts: ReactNode[] = [];
    let last = 0;
    let m: RegExpExecArray | null;
    TOKEN.lastIndex = 0;
    while ((m = TOKEN.exec(text))) {
      const before = text.slice(last, m.index);
      const after = text.slice(m.index + m[0].length);
      if (before) parts.push(before);
      const field = byKey.get(m[1]);
      if (field) {
        if (/[\p{L}\p{N},;:.!?)$%]$/u.test(before)) parts.push(" ");
        parts.push(<FieldChip key={`${m[1]}-${m.index}`} field={field} value={values[field.key] || ""} active={activeKey === field.key} onChange={onChange} onFocusField={onFocusField} lang={lang} />);
        if (/^[\p{L}\p{N}(]/u.test(after)) parts.push(" ");
      } else parts.push(BLANK);
      last = m.index + m[0].length;
    }
    if (last < text.length) parts.push(text.slice(last));
    return parts.length === 1 ? parts[0] : <>{parts.map((p, i) => <Fragment key={i}>{p}</Fragment>)}</>;
  };

  return (
    <div className="fs-paper" data-testid="model-paper" lang={lang === "en" ? "en" : "pt-BR"}>
      {Array.from(doc.body.childNodes).map((n, i) => <Fragment key={i}>{renderNode(n)}</Fragment>)}
    </div>
  );
}

interface ChipProps {
  field: ContractField;
  value: string;
  active: boolean;
  onChange: (key: string, value: string) => void;
  onFocusField: (key: string) => void;
  lang: string;
}

function FieldChip({ field, value, active, onChange, onFocusField, lang }: ChipProps) {
  const filled = value.trim().length > 0;
  const cls = `fs-blank${filled ? " is-filled" : ""}${active ? " is-active" : ""}`;
  if (INLINE_TYPES.has(field.type)) {
    const size = Math.min(60, Math.max(6, (filled ? value.length : field.label.length) + 1));
    return (
      <input
        className={cls}
        value={value}
        placeholder={field.label}
        size={size}
        onChange={(e) => onChange(field.key, e.target.value)}
        onFocus={() => onFocusField(field.key)}
        data-testid={`blank-${field.key}`}
        data-field={field.key}
        aria-label={field.label}
        inputMode={field.type === "money" || field.type === "number" || field.type === "percent" || field.type === "cpf" || field.type === "cnpj" || field.type === "cep" || field.type === "phone" ? "decimal" : "text"}
        spellCheck={false}
      />
    );
  }
  const shown = filled ? renderValue(field, value, lang) : null;
  return (
    <button type="button" className={`${cls} fs-blank-btn`} onClick={() => onFocusField(field.key)} data-testid={`blank-${field.key}`} data-field={field.key} title={field.label}>
      {shown ?? field.label}
    </button>
  );
}

/** The same document with the values baked in, editable as free text. Read `ref.current.innerHTML` when continuing. */
export const FreeTextView = forwardRef<HTMLDivElement, { html: string; fields: ContractField[]; values: Record<string, string>; lang?: string }>(
  function FreeTextView({ html, fields, values, lang = "pt" }, ref) {
    const filled = useMemo(() => fillTemplate(stripAiMarks(html), fields, values, lang), [html, fields, values, lang]);
    return (
      <div
        ref={ref}
        lang={lang === "en" ? "en" : "pt-BR"}
        className="fs-paper fs-paper-free"
        contentEditable
        suppressContentEditableWarning
        spellCheck
        data-testid="model-paper-free"
        dangerouslySetInnerHTML={{ __html: filled }}
      />
    );
  },
);

function escapeHtml(s: string): string {
  return s.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/"/g, "&quot;");
}

/** Values into the template, with the same spacing rule the API applies. */
export function fillTemplate(html: string, fields: ContractField[], values: Record<string, string>, lang = "pt"): string {
  const byKey = new Map(fields.map((f) => [f.key, f]));
  return html.replace(/([^\s<>{}]?)(\s*)\{\{(f\d+)\}\}(\s*)([^\s<>{}]?)/g, (_m, prevChar: string, ws1: string, key: string, ws2: string, nextChar: string) => {
    const field = byKey.get(key);
    const text = field ? renderValue(field, values[key], lang) : null;
    const value = text == null ? BLANK : `<span class="v">${escapeHtml(text)}</span>`;
    const before = prevChar && !ws1 && /[\p{L}\p{N},;:.!?)$%]/u.test(prevChar) ? " " : "";
    const after = nextChar && !ws2 && /[\p{L}\p{N}(]/u.test(nextChar) ? " " : "";
    return `${prevChar}${ws1}${before}${value}${after}${ws2}${nextChar}`;
  });
}
